import {
    Autocomplete, Button, TextField, FormControl, FormLabel,
    FormGroup, FormControlLabel, Checkbox, FormHelperText
} from "@mui/material";
import React from "react";
import { fileToBase64 } from "utils/fileToBase64";

export function FormikTextInput({ data, name, formik, disabled, className }) {
    return (
        <TextField
            label={data[name].label}
            name={name}
            placeholder={data[name].placeholder}
            margin="normal"
            type="text"
            disabled={disabled}
            value={formik.values[name]}
            onChange={formik.handleChange}
            error={formik.touched[name] && Boolean(formik.errors[name])}
            helperText={formik.touched[name] && formik.errors[name]}
            InputLabelProps={{ shrink: true }}
            fullWidth={className ? false : true}
            className={className}
        />
    );
}

export function FormikNumberInput({ data, name, formik, disabled, className }) {
    return (
        <TextField
            label={data[name].label}
            name={name}
            placeholder={data[name].placeholder}
            margin="normal"
            type="number"
            disabled={disabled}
            value={formik.values[name]}
            onChange={formik.handleChange}
            error={formik.touched[name] && Boolean(formik.errors[name])}
            helperText={formik.touched[name] && formik.errors[name]}
            InputLabelProps={{ shrink: true }}
            fullWidth={className ? false : true}
            className={className}
        />
    );
}

export function FormikSelectInput({
    data,
    name,
    formik,
    disabled,
    options,
    optionKey,
    optionValue,
    optionName,
    className,
}) {
    return (
        <TextField
            label={data[name].label}
            name={name}
            margin="normal"
            select
            disabled={disabled}
            SelectProps={{ native: true }}
            value={formik.values[name]}
            onChange={formik.handleChange}
            error={formik.touched[name] && Boolean(formik.errors[name])}
            helperText={formik.touched[name] && formik.errors[name]}
            InputLabelProps={{ shrink: true }}
            fullWidth={className ? false : true}
            className={className}
        >
            {options.map((option, index) => {
                return (
                    <option key={option[optionKey] || index} value={option[optionValue]}>
                        {option[optionName]}
                    </option>
                );
            })}
        </TextField>
    );
}

export function FormikYesOrNoInput({
    data,
    name,
    formik,
    disabled,
    className,
}) {
    return (
        <TextField
            label={data[name].label}
            name={name}
            margin="normal"
            select
            disabled={disabled}
            SelectProps={{ native: true }}
            value={formik.values[name]}
            onChange={formik.handleChange}
            error={formik.touched[name] && Boolean(formik.errors[name])}
            helperText={formik.touched[name] && formik.errors[name]}
            InputLabelProps={{ shrink: true }}
            fullWidth={className ? false : true}
            className={className}
        >
            <option value="Y">Si</option>
            <option value="N">No</option>
        </TextField>
    );
}

export function FormikDateInput({ data, name, formik, disabled, className }) {
    return (
        <TextField
            label={data[name].label}
            name={name}
            placeholder={data[name].placeholder}
            margin="normal"
            type="date"
            disabled={disabled}
            value={formik.values[name]}
            onChange={formik.handleChange}
            error={formik.touched[name] && Boolean(formik.errors[name])}
            helperText={formik.touched[name] && formik.errors[name]}
            InputLabelProps={{ shrink: true }}
            fullWidth={className ? false : true}
            className={className}
        />
    );
}

export function FormikTimeInput({ data, name, formik, disabled, className }) {
    return (
        <TextField
            label={data[name].label}
            name={name}
            placeholder={data[name].placeholder}
            margin="normal"
            type="time"
            disabled={disabled}
            value={formik.values[name]}
            onChange={formik.handleChange}
            error={formik.touched[name] && Boolean(formik.errors[name])}
            helperText={formik.touched[name] && formik.errors[name]}
            InputLabelProps={{ shrink: true }}
            fullWidth={className ? false : true}
            className={className}
        />
    );
}

export function FormikFileInput({ data, name, formik, disabled, className }) {
    const handleFile = async (e) => {
        const file = e.target.files[0];
        const fileData = await fileToBase64(file);
        formik.setFieldValue(name + "File", fileData);
    };

    return (
        <Button
            variant="contained"
            component="label"
            margin="normal"
            disabled={disabled}
            error={formik.touched[name] && Boolean(formik.errors[name])}
            fullWidth={className ? false : true}
            className={className}
        >
            {data[name].label}
            <input hidden type="file" name={name} onChange={handleFile} />
        </Button>
    );
}
export function FormikAutocompleteInput({
    data,
    name,
    formik,
    disabled,
    options,
    optionKey,
    optionValue,
    optionName,
    className,
    onChange,
}) {
    const value = options.find((o) => o[optionValue] === formik.values[name]) ?? null;

    return (
        <Autocomplete
            options={options}
            disableClearable
            getOptionLabel={(option) => option[optionName] ?? ""}
            value={value}
            disabled={disabled}
            onChange={(_, selected) => {
                formik.setFieldValue(name, selected?.[optionValue] ?? "");
                onChange?.(selected);
            }}
            isOptionEqualToValue={(option, val) => option[optionValue] === val[optionValue]}
            noOptionsText="Sin resultados"
            className={className}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={data[name].label}
                    placeholder={"Selecciona uno" || data[name].placeholder}
                    margin="normal"
                    error={formik.touched[name] && Boolean(formik.errors[name])}
                    helperText={formik.touched[name] && formik.errors[name]}
                    InputLabelProps={{ shrink: true }}
                    fullWidth={!className}
                />
            )}
        />
    );
}

export function FormikCheckboxGroup({
    data, name, formik, disabled, options, optionKey, optionValue, optionName, className
}) {
    const isError = formik.touched[name] && Boolean(formik.errors[name]);

    return (
        <FormControl
            component="fieldset"
            margin="normal"
            error={isError}
            disabled={disabled}
            fullWidth={!className}
            className={className}
        >
            <FormLabel component="legend" style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                {data[name]?.label}
            </FormLabel>
            <FormGroup row>
                {options.map((option, index) => (
                    <FormControlLabel
                        key={option[optionKey] || index}
                        label={option[optionName] || ''}
                        control={
                            <Checkbox
                                name={name}
                                value={option[optionValue]}
                                checked={formik.values[name].includes(option[optionValue]) || false}
                                onChange={formik.handleChange}
                            />
                        }
                    />
                ))}
            </FormGroup>
            {isError && <FormHelperText>{formik.errors[name]}</FormHelperText>}
        </FormControl>
    )

}

export function FormikAutocompleteMultipleInput({
    data,
    name,
    formik,
    disabled,
    options,
    optionKey,
    optionValue,
    optionName,
    className,
    onChange,
}) {
    const currentValues = formik.values[name] || [];
    const value = options.filter((o) => currentValues.includes(o[optionValue]));

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            options={options}
            getOptionLabel={(option) => option[optionName] ?? ""}
            value={value}
            disabled={disabled}
            onChange={(_, selectedArray) => {
                const newValues = selectedArray.map((item) => item[optionValue]);
                formik.setFieldValue(name, newValues);
                onChange?.(selectedArray);
            }}
            isOptionEqualToValue={(option, val) => option[optionValue] === val[optionValue]}
            noOptionsText="Sin resultados"
            sx={{ width: 200, marginRight: '1rem' }}

            // 1. Mantenemos los checkboxes nativos en la lista desplegable
            renderOption={(props, option, { selected }) => (
                <li {...props} style={{ padding: '4px 8px' }}>
                    <Checkbox
                        checked={selected}
                        style={{ marginRight: 8, padding: 4 }}
                        color="primary"
                    />
                    {option[optionName]}
                </li>
            )}

            renderTags={() => null}

            renderInput={(params) => (
                <TextField
                    {...params}
                    label={data[name].label}
                    //  El placeholder actúa como visor de estado
                    placeholder={
                        value.length > 0
                            ? `${value.length} seleccionado(s)`
                            : (data[name].placeholder || "Seleccionar...")
                    }
                    margin="normal"
                    error={formik.touched[name] && Boolean(formik.errors[name])}
                    helperText={formik.touched[name] && formik.errors[name]}
                    InputLabelProps={{ shrink: true }}
                    fullWidth={true}
                />
            )}
        />
    );
}